const SHARE_HASH_PREFIX = 's=';

interface ShareLocation {
  uploadId?: string;
  secret?: string;
}

function isLocalHost(hostname: string): boolean {
  if (!hostname) {
    return true;
  }

  const lower = hostname.toLowerCase();
  return lower === 'localhost' || lower === '127.0.0.1' || lower === '::1' || lower.endsWith('.localhost');
}

function getApexDomain(hostname: string): string {
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length <= 2) {
    return hostname;
  }
  return parts.slice(-2).join('.');
}

export function buildShareLink(uploadId: string, secret: string): string {
  const base = import.meta.env.VITE_SHARE_BASE_URL
    ? new URL(import.meta.env.VITE_SHARE_BASE_URL)
    : new URL(window.location.origin);

  const target = new URL(base.toString());

  if (!isLocalHost(target.hostname)) {
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const apexDomain = getApexDomain(target.hostname);
    
    // Check if current hostname is a custom domain (not main domain, not www, not a region subdomain)
    const isCustomDomain = currentHostname && 
                          currentHostname !== 'sendallfiles.com' && 
                          currentHostname !== 'www.sendallfiles.com' &&
                          !isRegionSubdomain(currentHostname);
    
    if (isCustomDomain) {
      // Use the current custom domain for the shareable link
      target.hostname = currentHostname;
      target.port = '';
    } else {
      // Use the region subdomain from the uploadId
      const subdomain = uploadId.slice(0, 4).toLowerCase();
      if (subdomain) {
        target.hostname = `${subdomain}.${apexDomain}`;
        target.port = '';
      }
    }
  }

  // Route format: /d/:uploadId with secret kept in the hash
  target.pathname = `/d/${encodeURIComponent(uploadId)}`;
  target.search = '';
  target.hash = `${SHARE_HASH_PREFIX}${secret}`;
  return target.toString();
}

function isRegionSubdomain(hostname: string): boolean {
  const REGION_SUBDOMAINS = ['weur', 'enam', 'apac', 'ocea'];
  const normalized = hostname.toLowerCase();
  
  // Check if it's a subdomain of sendallfiles.com
  if (normalized.endsWith('.sendallfiles.com')) {
    const subdomain = normalized.slice(0, normalized.length - '.sendallfiles.com'.length);
    if (subdomain && subdomain !== 'www' && !subdomain.includes('.')) {
      // Check if it's a known R2 region subdomain
      if (REGION_SUBDOMAINS.includes(subdomain)) {
        return true;
      }
      // Check if it's a 4-character Azure region code (all region codes are exactly 4 lowercase letters)
      if (subdomain.length === 4 && /^[a-z]{4}$/.test(subdomain)) {
        return true;
      }
    }
  }
  
  return false;
}

export function parseBrandingProfileId(uploadId: string): number | null {
  if (!uploadId || uploadId.length < 8) {
    return null;
  }

  // New format: {region (4)}{license (1)}{branding (optional <id>b)}{retention (4)}{userSegment}{random}
  const licenseIndex = 4; // after region
  if (licenseIndex >= uploadId.length) {
    return null;
  }

  const licenseChar = uploadId[licenseIndex];
  if (licenseChar !== 'a' && licenseChar !== 'f' && licenseChar !== 'p' && licenseChar !== 'e' && licenseChar !== 'o') {
    return null;
  }

  const brandingStart = licenseIndex + 1;
  if (brandingStart >= uploadId.length) {
    return null;
  }

  let cursor = brandingStart;
  let digits = '';
  while (cursor < uploadId.length) {
    const char = uploadId[cursor];
    if (char >= '0' && char <= '9') {
      digits += char;
      cursor += 1;
      continue;
    }
    break;
  }

  if (digits.length === 0) {
    return null;
  }

  if (cursor >= uploadId.length || uploadId[cursor] !== 'b') {
    return null;
  }

  const id = Number.parseInt(digits, 10);
  if (Number.isNaN(id) || id <= 0) {
    return null;
  }

  return id;
}

export function parseLicenseType(uploadId: string): string | null {
  if (!uploadId || uploadId.length < 5) {
    return null;
  }

  // New format: {region (4)}{license (1)}{branding (optional <id>b)}{retention (4)}{userSegment}{random}
  const pos = 4; // after region
  if (pos >= uploadId.length) {
    return null;
  }

  const licenseChar = uploadId[pos];
  if (licenseChar === 'a' || licenseChar === 'f' || licenseChar === 'p' || licenseChar === 'e' || licenseChar === 'o') {
    return licenseChar;
  }
  return null;
}

export function parseShareLocation(location: Location): ShareLocation {
  try {
    const url = new URL(location.href);
    let uploadId: string | undefined;
    const segments = url.pathname.split('/').filter(Boolean);
    if (segments.length >= 2 && segments[0] === 'd') {
      uploadId = decodeURIComponent(segments[1]);
    }
    const hash = url.hash.replace(/^#/, '');
    let secret: string | undefined;
    if (hash.startsWith(SHARE_HASH_PREFIX)) {
      secret = hash.slice(SHARE_HASH_PREFIX.length);
    } else if (hash.startsWith('secret=')) {
      secret = hash.slice('secret='.length);
    }

    return { uploadId, secret };
  } catch {
    return {};
  }
}
